import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ContactInfo from './components/ContactInfo';
import ContactForm from './components/ContactForm';
import TeamMembers from './components/TeamMembers';
import LocationMap from './components/LocationMap';
import Icon from '../../components/AppIcon';


const Contact = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbItems = [
    { label: 'Home', path: '/homepage' },
    { label: 'Contact', path: '/contact', isLast: true }
  ];

  return (
    <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 20 }}
          transition={{ duration: 0.8 }}
          className="bg-gradient-to-br from-primary/5 via-background to-accent/5 py-16"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Breadcrumb customItems={breadcrumbItems} />
            
            <div className="text-center max-w-4xl mx-auto">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-4xl md:text-5xl font-bold text-text-primary mb-6"
              >
                Contact Our Medical Device Experts
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-xl text-text-secondary mb-8 leading-relaxed"
              >
                Connect with Malaysia's leading distributor of endoscopic and surgical products. 
                Our GDPMD-certified team is ready to support your healthcare needs with expert guidance and premium medical solutions.
              </motion.p>
              
              {/* Quick Contact Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto"
              >
                <div className="bg-card/80 backdrop-blur-sm border border-border rounded-lg p-4">
                  <div className="text-2xl font-bold text-primary mb-1">24/7</div>
                  <div className="text-sm text-text-secondary">Emergency Support</div>
                </div>
                <div className="bg-card/80 backdrop-blur-sm border border-border rounded-lg p-4">
                  <div className="text-2xl font-bold text-primary mb-1">&lt;2hrs</div>
                  <div className="text-sm text-text-secondary">Response Time</div>
                </div>
                <div className="bg-card/80 backdrop-blur-sm border border-border rounded-lg p-4">
                  <div className="text-2xl font-bold text-primary mb-1">12+</div>
                  <div className="text-sm text-text-secondary">Years Experience</div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Main Contact Section */}
        <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Information */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <ContactInfo />
              </motion.div>

              {/* Contact Form */}
              <div>
                <ContactForm />
              </div>
            </div>
          </div>
        </section>

        {/* Location & Map Section */}
        <section className="py-16 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <LocationMap />
          </div>
        </section>

        {/* Team Members Section */}
        <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <TeamMembers />
          </div>
        </section>

        {/* Additional Support Section */}
        <section className="py-16 bg-gradient-to-br from-primary/5 to-accent/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <h2 className="text-3xl font-bold text-text-primary mb-8">
                Additional Support Resources
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    title: 'Product Catalog',
                    description: 'Download our comprehensive medical device catalog',
                    icon: 'FileText',
                    action: 'Download PDF',
                    color: 'bg-blue-500'
                  },
                  {
                    title: 'Training Programs',
                    description: 'Learn about our medical education initiatives',
                    icon: 'GraduationCap',
                    action: 'View Programs',
                    color: 'bg-green-500'
                  },
                  {
                    title: 'Service Support',
                    description: 'Equipment maintenance and technical support',
                    icon: 'Wrench',
                    action: 'Service Request',
                    color: 'bg-orange-500'
                  },
                  {
                    title: 'Partnership',
                    description: 'Explore business partnership opportunities',
                    icon: 'Handshake',
                    action: 'Learn More',
                    color: 'bg-purple-500'
                  }
                ]?.map((resource, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-card border border-border rounded-lg p-6 hover:shadow-medical-hover transition-all duration-300 group cursor-pointer"
                  >
                    <div className={`w-12 h-12 ${resource?.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon name={resource?.icon} size={24} color="white" />
                    </div>
                    <h3 className="text-lg font-semibold text-text-primary mb-2">
                      {resource?.title}
                    </h3>
                    <p className="text-text-secondary text-sm mb-4">
                      {resource?.description}
                    </p>
                    <div className="text-primary font-medium text-sm group-hover:text-primary/80 transition-colors duration-200">
                      {resource?.action} →
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
    </div>
  );
};

export default Contact;